<template>
    <v-data-table
        :headers="headers"
        :items="appointments"
        class="v-application--is-ltr"
        mobile-breakpoint="670"
        :items-per-page="25"
        @update:options="onOptionsChange"
        :server-items-length="counts"
        :loading="isLoading"
        :show-expand="isPharmacyRequest || isCanceledRequest"
        item-key="group_id"
        no-data-text="Keine Suchergebnisse"
        loading-text="Wird geladen..."
        disable-filtering
        :sort-by="defaultSortBy"
        sort-desc
        :footer-props="{
            showFirstLastPage: true,
            showCurrentPage: true,
            itemsPerPageOptions: [5, 10, 25, 50],
            itemsPerPageText: 'Zeilen pro Seite',
        }"
    >
        <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <v-btn
                v-if="isExpandableGroup(item)"
                icon
                small
                @click.stop="expand(!isExpanded)"
            >
                <v-icon small>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
        </template>
        <template v-slot:item.date="{ item }">
            <span :class="item.commited && 'committed'">
                {{ formatDateOnly(item.date) }}
            </span>
            <span v-if="isExpandableGroup(item)"  :class="item.commited && 'committed'">
                - {{ formatDateOnly(item.group_enddate) }} ({{ item.group_size }})
            </span>
            <div>
                <v-chip
                    v-for="service in item.available_services"
                    :key="service"
                    :color='getServiceDetails(service).color'
                    class="mr-1 px-2"
                    dark
                    :small="item.service === service"
                    :x-small="item.service !== service"
                    label
                >
                    {{ getServiceDetails(service).name }}
                </v-chip>
            </div>

        </template>
        <template v-slot:item.created="{ item }">
            <span
                :class="{'success white--text px-2 py-1 rounded': isCreatedInLast10Minutes(item.created)}"
            >
                {{ isCreatedInLast10Minutes(item.created) ? 'NEU - ' : ''  }} {{ formatCreatedRange(item) }}
            </span>
        </template>
        <template v-slot:item.request_date="{ item }">
            <span
                :class="{'success white--text px-2 py-1 rounded': isCreatedInLast10Minutes(item.request_date)}"
            >
                {{ isCreatedInLast10Minutes(item.request_date) ? 'NEU - ' : ''  }} {{ $formatDate(item.request_date) }}
            </span>
        </template>
        <template v-slot:item.last_fb_cancelled_at="{ item }">
            <span
                :class="{'success white--text px-2 py-1 rounded': isCreatedInLast10Minutes(item.last_fb_cancelled_at)}"
            >
                {{ isCreatedInLast10Minutes(item.last_fb_cancelled_at) ? 'NEU - ' : ''  }} {{ $formatDate(item.last_fb_cancelled_at) }}
            </span>
        </template>
        <template v-slot:item.assigned_pharmacist="{ item }">
            <a
                v-if="item.assigned_pharmacist?._id"
                :href="`/pharmacists/${item.assigned_pharmacist._id}`"
                target="_blank"
            >
                {{ item.assigned_pharmacist.firstname }} {{ item.assigned_pharmacist.lastname }}
            </a>
        </template>
        <template v-slot:item.requested_pharmacist="{ item }">
            <a
                v-if="item.requested_pharmacist?._id"
                :href="`/pharmacists/${item.requested_pharmacist._id}`"
                target="_blank"
            >
                {{ item.requested_pharmacist.firstname }} {{ item.requested_pharmacist.lastname }}
            </a>
        </template>
        <template v-slot:item.admin="{ item }">
            <span v-if="item.admin">{{ item.admin.firstname }} {{ item.admin.lastname }}</span>
        </template>
        <template v-slot:item.pharmacy="{ item }">
            <a
                v-if="pharmacyHref(item.pharmacy)"
                :href="pharmacyHref(item.pharmacy)"
                target="_blank"
            >
                {{ pharmacyLabel(item.pharmacy) }}
            </a>
            <span v-else>{{ pharmacyLabel(item.pharmacy) }}</span>
        </template>
        <template v-slot:item.wf_state="{ item }">
            <span>
                {{
                    translateWfState(item.wf_state)
                }}
                <v-icon
                    v-if="item.self_assigned"
                    color="secondary"
                    size="20"
                    v-bind="attrs"
                    v-on="on"
                >
                    mdi-account-check
                </v-icon>
            </span>
        </template>
        <template v-slot:item.actions="{ item }">
            <PharmacistsRequests
                :appointment="item"
                :pharmacy="item.pharmacy"
                :assignedPharmacist="item.assigned_pharmacist"
                :knownPharmacists="item.pharmacy.known_pharmacists || []"
                :numOfRequests="item.pharmacist_requests_count"
            />
        </template>
        <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
                <v-simple-table dense class="group-appointments-table">
                    <thead>
                        <tr>
                            <th>Datum</th>
                            <th>Termin angelegt</th>
                            <th>Service</th>
                            <th>Zustand</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="appointment in item.group_appointments" :key="appointment._id">
                            <td>{{ formatDateOnly(appointment.date) }}</td>
                            <td>{{ $formatDate(appointment.created) }}</td>
                            <td>
                                <v-chip
                                    v-if="appointment.service"
                                    :color='getServiceDetails(appointment.service).color'
                                    class="mr-1 px-2"
                                    dark
                                    x-small
                                    label
                                >
                                    {{ getServiceDetails(appointment.service).name }}
                                </v-chip>
                            </td>
                            <td>{{ translateWfState(appointment.wf_state || item.wf_state) }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </td>
        </template>
    </v-data-table>
</template>

<script>
import moment from 'moment'
import PharmacistsRequests from '../pharmacist/requests/PharmacistsRequests.vue';

export default {
    components: {
        PharmacistsRequests
    },
    props: {
        requestsData: Object,
        isPharmacyRequest: Boolean,
        isCanceledRequest: Boolean,
        isLoading: Boolean,
    },
    data() {
        return {
            headers: [
                { text: 'Datum', value: 'date' },
                { text: 'Zustand', value: 'wf_state' },
                { text: 'Zugewiesener Freiberufler', value: 'assigned_pharmacist' },
                { text: 'Admin', value: 'admin' },
                { text: 'Apotheke', value: 'pharmacy' },
                { text: '', value: 'actions' },
            ],
        }
    },
    computed: {
        appointments() {
            return this.requestsData?.appointments || []
        },
        counts() {
            return this.requestsData?.total_results || 0
        },
        defaultSortBy() {
            if (this.isCanceledRequest) return 'last_fb_cancelled_at'
            return this.isPharmacyRequest ? 'created' : 'request_date'
        }
    },
    methods: {
        isExpandableGroup(item) {
            return (this.isPharmacyRequest || this.isCanceledRequest)
                && (item.group_appointments?.length || 0) > 1;
        },
        formatDateOnly(date) {
            if (!date) return '';
            if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
                return date.split('-').reverse().join('-');
            }
            return this.$formatDate(date, 'DD-MM-YYYY');
        },
        formatCreatedRange(item) {
            if (!item?.created) return '';
            if (!item.created_enddate) return this.$formatDate(item.created);
            return `${this.$formatDate(item.created)} - ${this.$formatDate(item.created_enddate)}`;
        },
        onOptionsChange(opts) {
            if (this.isLoading) return // prevent redundant fetches
            this.$emit('fetch-requests', {
                page: opts.page,
                page_size: opts.itemsPerPage,
                sort_by: opts.sortBy[0],
                sort_asc: opts.sortDesc[0] ? -1 : 1,
            });
        },
        translateWfState(state) {
            const states = {
                initial: 'Entwurf',
                inquired: 'Angefragt',
                open: 'In Bearbeitung',
                allocated: 'Vergeben',
                completed: 'Abgeschlossen',
                apobilanz: 'Apobilanz',
                tempInvoice: 'In vorl. Rechnung',
                invoice: 'In Rechnung',
                paid: 'Bezahlt',
                expired: 'Abgelaufen',
                deleted: 'Gelöscht',
            };
            return states[state] || state; // Fallback: Originalwert, falls nicht übersetzt
        },
        pharmacyHref(pharmacy) {
            const pharmacyId = pharmacy?._id || pharmacy?.pharmacy_id || pharmacy?.id;
            return pharmacyId ? `/pharmacies/${pharmacyId}` : null;
        },
        pharmacyLabel(pharmacy) {
            return pharmacy?.name || pharmacy?.shortname || '';
        },
        getServiceDetails(service='substitution') {
            const servicesColors = {
                substitution: {'color': 'cyan', 'name': "Apotheker/in"},
                pta: {'color': 'warning', 'name': "PTA"},
                pka: {'color': 'success', 'name': "PKA"},
                qms: {'color': 'red', 'name': "QMS"},
                storno: {'color': 'red', 'name': "Storno"},
            }
            return servicesColors[service] || {'color': 'grey', 'name': service}
        },
        isCreatedInLast10Minutes(date) {
            let newDate = date;
            if (!date) return false;
            if (!newDate.includes('+')) newDate += 'Z';
            return moment().diff(moment(newDate), 'minutes') < 10
        },
    },
    created() {
        //  Add new table columns based on whether it's a pharmacy or a freelancer request
        if (this.isPharmacyRequest) {
            this.headers.splice(1, 0, { text: 'Termin angelegt', value: 'created' })
        } else if (this.isCanceledRequest) {
            this.headers.splice(1, 0, { text: 'Letztes FB-Storno', value: 'last_fb_cancelled_at' })
        } else {
            this.headers.splice(1, 0,
                { text: 'Anfragedatum', value: 'request_date' },
                { text: 'Anfragen Freiberufler', value: 'requested_pharmacist' }
            )
        }
    }
}
</script>
<style>
    .committed {
        background-color: #FCFB93;
    }
</style>
